/**
 * Shared Tailwind class strings for the TopToolbar picker dropdowns
 * (Domain / HeatExchanger / Wheels / ImplicitPrimitives / NasaLattice /
 * SupinInlet). These class strings were previously duplicated verbatim
 * across the `topToolbar/*Dropdown.tsx` subcomponents; extracting them here
 * keeps the rendered styling byte-identical while giving the picker dropdowns
 * a single source of truth.
 *
 * Each constant below is the exact, character-identical string that was
 * inlined before — composed via `cn(...)` at the call sites exactly as
 * before, so the applied className is unchanged.
 *
 * NOTE: AppMenuBar's `menuItemClass` / `dropItemClass` / `dropContentClass`
 * are a different dropdown family (the menu / account / notification menus)
 * and share no byte-identical string with the picker dropdowns, so they are
 * intentionally NOT folded in here. See ## DRIFT NOTES in the PR.
 */

/** DropdownMenuContent wrapper for every picker dropdown. */
export const pickerContentClass =
  'flex flex-col gap-1 bg-transparent p-1 shadow-none';

/**
 * Item base for heroed picker cards that are always full-height
 * (Domain / HeatExchanger / Wheels).
 */
export const pickerHeroItemBaseClass =
  'flex h-20 items-center justify-between overflow-hidden rounded-md border border-[#737373] bg-telekinesis-bg-dark px-4 hover:cursor-pointer hover:bg-[#0f3d24] focus:bg-[#0f3d24]';

/**
 * Item base for pickers whose rows can be disabled and whose height varies
 * per-row (NasaLattice / SupinInlet). Height / flex modifiers are appended
 * per-row at the call site.
 */
export const pickerDisablableItemBaseClass =
  'overflow-hidden rounded-md border border-[#737373] bg-telekinesis-bg-dark px-4 focus:bg-[#0f3d24]';

/** Active (currently-selected) picker item state. */
export const pickerItemActiveClass =
  'border-[#107d40] text-[#107d40] focus:border-[#107d40] focus:text-[#107d40]';

/** Inactive (not-selected) picker item state. */
export const pickerItemInactiveClass =
  'text-telekinesis-neutral-50 focus:border-[#737373]';

/** Disabled ("coming soon") picker item state. */
export const pickerItemDisabledClass =
  'cursor-default text-telekinesis-neutral-500 opacity-50 focus:bg-telekinesis-bg-dark';

/** Wrapper around a picker item's hero image. */
export const pickerHeroWrapperClass =
  'flex h-full w-20 items-center justify-center';

/**
 * Fixed width for every feature-tree PILL trigger in the top toolbar row —
 * the domain dropdown and every sub-tab pill (plain or dropdown-backed) share
 * this exact width, so the row reads as one consistent set of controls
 * instead of pills sized to their own content. The Rocket Nozzle pill is the
 * reference: this is its natural (pre-standardization) rendered width.
 *
 * A plain `width` on a flex item is only its flex-basis: when a domain has
 * more sub-tabs than the row can fit (e.g. Land's 4), every pill shrinks
 * BELOW 184px together to fit (the default flex-shrink) rather than any one
 * pill overflowing the row — so this is deliberately NOT paired with
 * `flex-shrink-0`. Every pill's text uses `truncate` (see each component) so
 * that graceful shrink degrades to an ellipsis, never a wrap.
 */
export const pillWidthClass = 'w-[184px]';
